import "./login.scss";
import React, { useState, useEffect } from 'react';
import axiosInstance from '../../api/axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthProvider';
import { setAccessToken, setRefreshToken, removeTokens } from '../../api/auth';
import { getInstructorId, getStudentId } from '../../services/thesisAssignedService';
import { jwtDecode } from "jwt-decode";
import { toast } from 'react-toastify';
import { GoogleLogin } from '@react-oauth/google';

// ---- helpers ----
const normalizeRoles = (roles) => {
  if (Array.isArray(roles)) return roles;
  if (roles == null) return [];
  if (typeof roles === 'string') {
    // Sometimes roles arrive like 'ROLE_USER,ROLE_EXAMINER'
    // or even as a JSON string array '["ROLE_USER","ROLE_EXAMINER"]'
    try {
      const parsed = JSON.parse(roles);
      if (Array.isArray(parsed)) return parsed;
    } catch (_) {}
    return roles.split(',').map(r => r.trim()).filter(Boolean);
  }
  return [];
};

// Only show roles that actually change UI
const DISPLAYABLE_ROLES = new Set(['ROLE_ADMIN','ROLE_EXAMINER','ROLE_REVIEWER','ROLE_STUDENT']);
const filterDisplayable = (roles) => normalizeRoles(roles).filter(r => DISPLAYABLE_ROLES.has(r));

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const { isAuthenticated, setIsAuthenticated } = useAuth();
  const [error, setError] = useState('');
  const token = localStorage.getItem('token');

  // state for multiple-role selection
  const [availableRoles, setAvailableRoles] = useState([]);
  const [showRoleSelector, setShowRoleSelector] = useState(false);
  const [cachedUserName, setCachedUserName] = useState(null);

  useEffect(() => {
    setError('');
    if (!token || !isAuthenticated) return;
    const decoded = jwtDecode(token);
    const rolesArr = normalizeRoles(decoded?.roles);

    if (rolesArr.includes('ROLE_STUDENT')) {
      navigate('/student');
    } else if (rolesArr.includes('ROLE_EXAMINER') || rolesArr.includes('ROLE_REVIEWER')) {
      navigate('/'); // your previous behavior
    }
  }, []); // eslint-disable-line

  const proceedWithRole = async (role, userName) => {
    try {
      if (role === 'ROLE_EXAMINER' || role === 'ROLE_REVIEWER') {
        const idResponse = await getInstructorId(userName);
        if (idResponse.data && idResponse.data !== -1) {
          localStorage.setItem('examinerId', idResponse.data);
          localStorage.setItem('activeRole', role);
          navigate('/examiner');
        } else {
          throw new Error("Failed to fetch examinerId");
        }
      } else if (role === 'ROLE_STUDENT') {
        const idResponse = await getStudentId(userName);
        if (idResponse.data && idResponse.data !== -1) {
          localStorage.setItem('studentId', idResponse.data);
          localStorage.setItem('activeRole', role);
          navigate('/student');
        } else {
          throw new Error("Failed to fetch studentId");
        }
      } else if (role === 'ROLE_ADMIN') {
        localStorage.setItem('activeRole', role);
        navigate('/');
      } else {
        throw new Error("Unknown role. Access denied.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Role processing failed.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosInstance.post('/auth/signin', { username, password });

      if (response && response.response && response.response.status === 401) {
        setError(response.response.data.message);
        return;
      }

      setIsAuthenticated(true);

      const accessToken = response.data.accessToken;
      const refreshToken = response.data.refreshToken;
      const userId = response.data.id;

      localStorage.setItem("userId", userId);
      setAccessToken(accessToken);
      setRefreshToken(refreshToken);
      localStorage.setItem('token', accessToken); // ensure token is available for later

      const decoded = jwtDecode(accessToken);
      const userName = decoded?.sub || null;
      const rolesArr = filterDisplayable(decoded?.roles);

      if (!userName) throw new Error("No username found in token!");

      if (rolesArr.length > 1) {
        // show selector
        setCachedUserName(userName);
        setAvailableRoles(rolesArr);
        setShowRoleSelector(true);
        return;
      }

      // single or zero (fallback) role path
      if (rolesArr.length === 1) {
        await proceedWithRole(rolesArr[0], userName);
      } else {
        // fallback to previous logic if only generic roles were present
        navigate('/');
      }

    } catch (err) {
      console.error(err);
      setError('An error occurred. Please try again.');
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    const idToken = credentialResponse.credential;
    try {
      const res = await axiosInstance.post('/google-login', { idToken });

      const jwt = res.data.data?.accessToken || res.data.accessToken;
      if (!jwt) throw new Error('Invalid response - no token found!');

      const decoded = jwtDecode(jwt);
      const userName = decoded?.sub;
      const rolesArr = filterDisplayable(decoded?.roles);

      if (!userName) throw new Error("No username found in token!");

      localStorage.setItem('token', jwt);
      setIsAuthenticated(true);

      if (rolesArr.length > 1) {
        setCachedUserName(userName);
        setAvailableRoles(rolesArr);   // <-- guaranteed array now
        setShowRoleSelector(true);
        return;
      }

      if (rolesArr.length === 1) {
        await proceedWithRole(rolesArr[0], userName);
      } else {
        throw new Error("No roles assigned.");
      }

    } catch (err) {
      console.error(err);
      toast.error('Google Login failed.');
    }
  };

  return (
    <div className="login-container">
      <h2>Login</h2>

      <form onSubmit={handleSubmit}>
        <div>
          <label>Username</label>
          <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} />
        </div>

        <div>
          <label>Password</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>

        {error && <div className="error-message">{error}</div>}

        <button type="submit">Login</button>

        <p>OR You can login with a Google Account</p>
        <GoogleLogin
          onSuccess={handleGoogleLogin}
          onError={() => toast.error('Google Login Failed')}
        />
      </form>

      {showRoleSelector && (
        <div className="role-selector">
          <h3>Select your role to continue</h3>
          {availableRoles.map((r) => (
            <button
              key={r}
              onClick={() => {
                setShowRoleSelector(false);
                proceedWithRole(r, cachedUserName);
              }}
            >
              {r.replace('ROLE_', '')}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default Login;
